import React, { useRef, useCallback } from "react";
import "../styles/codeInput.scss";

/**
 * CodeInput — 4-digit OTP/WatchCode input
 * Figma: code-input-fields component (24654:3833)
 * 4 individual 64x64 boxes, gap 16px, centered digits
 * Font: Poppins 48px/60px regular, color #ffffff
 * Box: #262626 bg, #404040 border, 8px radius
 */
const CodeInput = ({ length = 4, value = "", onChange, error = false }) => {
  const inputRefs = useRef([]);

  const handleChange = useCallback(
    (index, e) => {
      const val = e.target.value;
      // Only allow single digit
      if (val && !/^\d$/.test(val)) return;

      const newCode = value.split("");
      newCode[index] = val;
      const result = newCode.join("");
      onChange(result);

      // Auto-focus next input
      if (val && index < length - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    },
    [value, onChange, length]
  );

  const handleKeyDown = useCallback(
    (index, e) => {
      if (e.key === "Backspace" && !value[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
      if (e.key === "ArrowLeft" && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
      if (e.key === "ArrowRight" && index < length - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    },
    [value, length]
  );

  const handlePaste = useCallback(
    (e) => {
      e.preventDefault();
      const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, length);
      onChange(pasted);
      // Focus the next empty or last input
      const focusIndex = Math.min(pasted.length, length - 1);
      inputRefs.current[focusIndex]?.focus();
    },
    [onChange, length]
  );

  return (
    <div className={`mango-code-input ${error ? "mango-code-input--error" : ""}`}>
      {Array.from({ length }).map((_, i) => (
        <input
          key={i}
          ref={(el) => (inputRefs.current[i] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          className={`mango-code-input__box ${value[i] ? "mango-code-input__box--filled" : ""}`}
          value={value[i] || ""}
          onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={i === 0 ? handlePaste : undefined}
          autoFocus={i === 0}
        />
      ))}
    </div>
  );
};

export default CodeInput;
